import type { PublicOptions } from '@pa/api/public'
import { Avatar, fmtCountdown, fmtLongDateOf, fmtTime, Select } from '@pa/ui'
import { ArrowLeft, Building2, CalendarClock, Check, PencilLine, Timer, Video } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ChallengeBox, Honeypot, useBotInputs, type BotInputs, type Recaptcha } from '@/features/bot'
import { ScreenTitle } from '@/features/entry'
import { WRITE_REASON, type HeldTime, type ReasonDraft } from '../model/machine'
import type { Choice } from '../model/resolve'

/**
 * Seconds left on the hold (Frontend §4.9): `expiresAt − serverTime` at the moment the hold answer arrived, minus the
 * `performance.now()` time since then. The device clock is never read, so a wrong phone clock can't change it.
 */
export function useHoldCountdown(hold: HeldTime): number {
  const secondsLeft = useCallback(() => {
    const total = (Date.parse(hold.expiresAt) - Date.parse(hold.serverTime)) / 1000
    return Math.max(0, Math.ceil(total - (performance.now() - hold.receivedAt) / 1000))
  }, [hold])
  const [left, setLeft] = useState(secondsLeft)
  useEffect(() => {
    const t = setInterval(() => {
      setLeft(secondsLeft())
    }, 250)
    return () => {
      clearInterval(t)
    }
  }, [secondsLeft])
  return left
}

/**
 * P-09 "Review and confirm" (UserFlows P-09), ported from the mockup's `DetailsStep`: the hold banner with the m:ss
 * countdown (warning style under 1:00; screen readers hear it at 1:00 and at expiry only), the review card with
 * Change, the optional reason (dropdown, text box with a 500 counter, "+ {reason}" chips) and Confirm. At 0 the hold
 * is released and the patient goes back to P-08 (`onExpired`). The honeypot and P-13 are tasks 15 / 16.
 */
export function ReviewStep({
  stepLabel,
  options,
  choice,
  hold,
  reason,
  reasonMaxLength,
  timeZone,
  onReason,
  onBack,
  onExpired,
  onConfirm,
  recaptcha,
  challenge = false,
  onChallengeUnavailable,
}: {
  stepLabel: string
  options: PublicOptions
  choice: Choice
  hold: HeldTime
  reason: ReasonDraft
  reasonMaxLength: number
  timeZone: string
  onReason: (patch: Partial<ReasonDraft>) => void
  onBack: () => void
  onExpired: () => void
  /**
   * With P-09's bot inputs (the honeypot, the fill time from this screen's first render and a fresh score token,
   * Frontend §4.6), resolved once the token is in (≤ 3 s).
   */
  onConfirm: (bot: Promise<BotInputs>) => void
  /** The clinic's reCAPTCHA (task 16). */
  recaptcha: Recaptcha
  /** P-13 beside Confirm after the booking's `challenge_required`; the countdown keeps running. */
  challenge?: boolean
  /** P-13's widget can't load (after its one retry) → P-14. */
  onChallengeUnavailable?: () => void
}) {
  const left = useHoldCountdown(hold)
  const bot = useBotInputs(recaptcha, 'book')
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const blockedByChallenge = challenge && challengeToken === null
  const fired = useRef(false)
  useEffect(() => {
    if (left <= 0 && !fired.current) {
      fired.current = true
      onExpired()
    }
  }, [left, onExpired])
  const reasonLabel = useId()
  const provider = options.providers.find((p) => p.id === choice.providerId)
  const location = options.locations.find((l) => l.id === choice.locationId)
  const type = options.visitTypes.find((t) => t.id === choice.visitTypeId)
  const tele = type?.telehealth === true
  const writing = reason.choice === WRITE_REASON
  const announce = left <= 0 ? 'Your hold ran out.' : left <= 60 ? 'One minute left on your hold.' : ''

  return (
    <>
      <div>
        <div className="pt-step-label">{stepLabel}</div>
        <ScreenTitle className="pt-title">Review and confirm</ScreenTitle>
        <Honeypot {...bot.honeypot} />
      </div>
      <div className="pt-body" style={{ display: 'grid', gap: 24 }}>
        <div className={`hold-banner ${left < 60 ? 'warn' : ''}`}>
          <Timer aria-hidden />
          <span>
            We’re holding <b>{fmtTime(hold.start, timeZone)}</b> for you so no one else can take it.
          </span>
          <span className="timer" aria-hidden>
            {fmtCountdown(left)}
          </span>
          <span className="sr-only" role="status">
            {announce}
          </span>
        </div>

        <div className="review">
          <div className="review-row">
            <span className="r-ic" aria-hidden>
              <CalendarClock />
            </span>
            <div className="grow">
              <span className="r-label">Date & time</span>
              <b>
                {fmtLongDateOf(hold.start, timeZone)} at {fmtTime(hold.start, timeZone)}
              </b>
            </div>
            <button type="button" className="btn btn-ghost btn-sm edit" onClick={onBack}>
              Change
            </button>
          </div>
          {provider && (
            <div className="review-row">
              <Avatar name={provider.name} color={provider.color} />
              <div className="grow">
                <span className="r-label">{type?.name} with</span>
                <b>{[provider.name, provider.credentials].filter((v) => v !== '').join(', ')}</b>
              </div>
            </div>
          )}
          <div className="review-row">
            <span className="r-ic" aria-hidden>
              {tele ? <Video /> : <Building2 />}
            </span>
            <div className="grow">
              <span className="r-label">{tele ? 'Video visit' : 'Location'}</span>
              <b>{tele ? 'You’ll get a secure video link before your visit' : location?.name}</b>
              {!tele && location?.address !== undefined && location.address !== '' && (
                <span className="muted r-sub">{location.address}</span>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="section-label" id={reasonLabel}>
            Reason for visit <span className="opt">Optional · helps your provider prepare</span>
          </div>
          <Select
            value={reason.choice}
            labelledBy={reasonLabel}
            onChange={(v) => {
              onReason({ choice: v })
            }}
            options={[
              {
                value: WRITE_REASON,
                label: 'Let me write the reason',
                icon: <PencilLine size={17} color="var(--blue)" aria-hidden />,
              },
              ...options.visitReasons.map((r) => ({ value: r, label: r, group: 'Common reasons' })),
            ]}
          />
          {writing && (
            <div style={{ marginTop: 10 }}>
              <textarea
                className="textarea"
                aria-label="Reason for visit"
                placeholder="Briefly describe any problems or symptoms…"
                value={reason.text}
                maxLength={reasonMaxLength}
                onChange={(e) => {
                  onReason({ text: e.target.value })
                }}
              />
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div className="quick-chips">
                  {options.visitReasons.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className="chip"
                      onClick={() => {
                        onReason({ text: (reason.text ? `${reason.text}; ${r}` : r).slice(0, reasonMaxLength) })
                      }}
                    >
                      + {r}
                    </button>
                  ))}
                </div>
                <span className={`char-count ${reason.text.length >= reasonMaxLength ? 'max' : ''}`}>
                  {reason.text.length}/{reasonMaxLength}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
      {challenge && (
        <ChallengeBox
          recaptcha={recaptcha}
          action="book"
          resetKey={0}
          onToken={setChallengeToken}
          onUnavailable={() => {
            onChallengeUnavailable?.()
          }}
        />
      )}
      <div className="pt-actions">
        <button type="button" className="back-link" aria-label="Back" onClick={onBack}>
          <ArrowLeft aria-hidden /> <span className="bl-text">Back</span>
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-accent btn-lg"
          disabled={blockedByChallenge}
          onClick={() => {
            onConfirm(bot.collect(challenge && challengeToken !== null ? challengeToken : undefined))
          }}
        >
          <Check aria-hidden /> Confirm<span className="bl-text"> appointment</span>
        </button>
      </div>
    </>
  )
}
